import { IconExternalFilled, IconSearchOutline } from '@zillow/constellation-icons';
import {
  AdornedInput,
  Alert,
  Box,
  Button,
  ButtonGroup,
  Checkbox,
  Icon,
  Image,
  Label,
  LabeledControl,
  LoadingMask,
  Page,
  Pagination,
  Spinner,
  Switch,
  Table,
  Text,
  ToastProvider,
  useToast,
} from '@zillow/constellation-10';

import type { PropertyEliseDto } from '@zg-rentals/ts-ad-management-client';
import { AdManagementEliseAiApiHandler_getEliseStatuses_Path } from '@zg-rentals/ts-ad-management-client';
import type { TabbedPageComponentProps } from '..';
import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useMemo, useState } from 'react';
import {
  getSharingStatusForProperty,
  handleBulkAction,
  onPropertyToggle,
  useGetEliseStatuses,
} from './OptOutEliseAi.utils';
import EliseAiImage from '@/assets/elise-ai-logo.png';
import { EliseAiStatusTag } from '@/shared/components/EliseAiStatusTag';
import { ColumnHeaderHelp } from '@/shared/components/ColumnHeaderHelp';
import { debounce } from '@zg-rentals/zdash';
import {
  useAdManagementEliseAiApiHandler_updateAllEliseAiStatuses,
  useAdManagementEliseAiApiHandler_updateEliseAiStatus,
} from '@zg-rentals/ts-ad-management-client-react-query';
import { useQueryClient } from '@zg-rentals/react-query5';
import { LoadingConstants } from '@zg-rentals/rental-manager-env';

const MANAGE_ELISE_EXTERNAL_URL = 'https://app.meetelise.com/';

const EliseImageContainer = () => (
  <Box
    css={{
      display: 'block',
      backgroundColor: 'black',
      borderRadius: '15%',
      height: '40px',
      width: '50px',
      padding: '14px 2px',
    }}
  >
    <Image src={EliseAiImage.src} />
  </Box>
);

export const ElisePropertyView = ({ propertyName, address, photoUrl }: PropertyEliseDto) => {
  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'row',
        gap: 'md',
      }}
    >
      <Image src={photoUrl} css={{ width: '100px', height: '75px' }} />
      <Box css={{ display: 'flex', flexDirection: 'column' }}>
        <Text>
          <b>{propertyName}</b>
        </Text>
        <Text>{address}</Text>
      </Box>
    </Box>
  );
};

const EliseDataTable = ({
  properties,
  selectedRows,
  onRowSelection,
  onSelectAll,
  isAllSelected,
  isIndeterminate,
  searchString,
  updateEliseAiStatusMutation,
}: {
  properties: Array<PropertyEliseDto>;
  selectedRows: Set<string>;
  onRowSelection: (propertyName: string, isSelected: boolean) => void;
  onSelectAll: (isSelected: boolean) => void;
  isAllSelected: boolean;
  isIndeterminate: boolean;
  searchString: string;
  updateEliseAiStatusMutation: ReturnType<typeof useAdManagementEliseAiApiHandler_updateEliseAiStatus>;
}) => {
  const { enqueueToast } = useToast();
  const updateActivationForProperty = useCallback(
    debounce(
      (property: PropertyEliseDto, enqueueToast: (t: JSX.Element) => void) =>
        onPropertyToggle(property, enqueueToast, updateEliseAiStatusMutation),
      1000,
    ),
    [updateEliseAiStatusMutation],
  );

  const mapPropertyToRow = (property: PropertyEliseDto, i: number) => {
    const isSharing = getSharingStatusForProperty(property);
    return (
      <Table.Row interactive key={property.listingAlias}>
        <Table.Cell>
          <Checkbox
            aria-label={`Select row ${i}`}
            checked={selectedRows.has(property.listingAlias)}
            onChange={() => onRowSelection(property.listingAlias, !selectedRows.has(property.listingAlias))}
          />
        </Table.Cell>
        <Table.Cell>
          <ElisePropertyView {...property} />
        </Table.Cell>
        <Table.Cell>
          <EliseAiStatusTag property={property} />
        </Table.Cell>
        <Table.Cell>
          <LabeledControl
            control={
              <Switch checked={isSharing} onChange={() => updateActivationForProperty(property, enqueueToast)} />
            }
            label={<Label>{isSharing ? 'On' : 'Off'}</Label>}
            size="md"
          />
        </Table.Cell>
      </Table.Row>
    );
  };

  const NoPropertiesFoundRow = () =>
    searchString.length > 0 &&
    properties.length === 0 && (
      <Table.Row>
        <Table.Cell
          css={{
            width: '80vw',
            height: '10vh',
            textAlign: 'center',
            border: '1px solid',
            borderColor: 'gray.300',
            borderTopColor: 'white',
          }}
        >
          <Text textStyle="body">
            <b>No properties found for &quot;{searchString}&quot;</b>
          </Text>
          <br />
          <Text textStyle="body">
            Try adjusting your search — you can sfearch by name, address, city, state, or ZIP code.
          </Text>
        </Table.Cell>
      </Table.Row>
    );

  return (
    <>
      <Table.Root
        alignments={['start', 'start', 'start']}
        appearance="horizontal"
        aria-label="Property enabled table"
        size="md"
        css={{ border: '1px solid', borderColor: 'gray.300', width: '100%' }}
      >
        <Table.Header>
          <Table.Row>
            <Table.HeaderCell css={{ width: '20px' }}>
              <Checkbox
                aria-label="Toggle selecting all rows"
                checked={isAllSelected}
                indeterminate={isIndeterminate}
                onChange={() => onSelectAll(!isAllSelected)}
              />
            </Table.HeaderCell>
            <Table.HeaderCell>Property</Table.HeaderCell>
            <Table.HeaderCell>
              <Box css={{ display: 'inline-flex', alignItems: 'center', gap: 'xs' }}>
                EliseAI status
                <ColumnHeaderHelp
                  title="EliseAI status"
                  body="This indicates whether you're receiving listing data from EliseAI. To make changes, manage data sharing directly on EliseAI."
                />
              </Box>
            </Table.HeaderCell>
            <Table.HeaderCell>
              <Box css={{ display: 'inline-flex', alignItems: 'center', gap: 'xs' }}>
                Data sharing
                <ColumnHeaderHelp
                  title="Data sharing"
                  body="Data sharing must be turned on for EliseAI to work on Zillow."
                />
              </Box>
            </Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Table.Body>{properties.map(mapPropertyToRow)}</Table.Body>
      </Table.Root>
      <NoPropertiesFoundRow />
    </>
  );
};

const BulkActionsAndInfo = ({
  fetchedProperties,
  searchedProperties,
  shownPropertiesString,
  selectedRows,
  isSearching,
  setLoadingBulkChange,
  updateAllEliseAiStatusesMutation,
  updateEliseAiStatusMutation,
}: {
  fetchedProperties: Array<PropertyEliseDto>;
  searchedProperties: Array<PropertyEliseDto>;
  shownPropertiesString: string;
  selectedRows: Set<string>;
  isSearching: boolean;
  setLoadingBulkChange: Dispatch<SetStateAction<LoadingConstants>>;
  updateAllEliseAiStatusesMutation: ReturnType<typeof useAdManagementEliseAiApiHandler_updateAllEliseAiStatuses>;
  updateEliseAiStatusMutation: ReturnType<typeof useAdManagementEliseAiApiHandler_updateEliseAiStatus>;
}) => {
  const { enqueueToast } = useToast();

  const SelectedInfoText = () => (
    <Text>
      Selecting <b>{selectedRows.size}</b> of <b>{fetchedProperties.length} properties</b>
    </Text>
  );
  const ShowingInfoText = () => (
    <Text>
      Showing <b>{shownPropertiesString}</b> of <b>{fetchedProperties.length} properties</b>
    </Text>
  );

  const onBulkAction = (action: 'activate' | 'deactivate') => {
    setLoadingBulkChange(LoadingConstants.LOADING);
    handleBulkAction(
      action,
      isSearching ? searchedProperties : fetchedProperties,
      selectedRows,
      isSearching,
      enqueueToast,
      setLoadingBulkChange,
      updateAllEliseAiStatusesMutation,
      updateEliseAiStatusMutation,
    );
  };

  return (
    <Box
      css={{
        display: 'flex',
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'center',
        height: '6vh',
      }}
    >
      {selectedRows.size > 0 ? <SelectedInfoText /> : <ShowingInfoText />}
      {selectedRows.size > 0 && (
        <Box css={{ marginBottom: 'md' }}>
          <ButtonGroup aria-label="Bulk Options">
            <Button onClick={() => onBulkAction('activate')}>Activate Sharing</Button>
            <Button onClick={() => onBulkAction('deactivate')}>Deactivate Sharing</Button>
          </ButtonGroup>
        </Box>
      )}
    </Box>
  );
};

export const OptOutEliseAiPage: React.FC<TabbedPageComponentProps> = () => {
  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [searchString, setSearchStringState] = useState('');
  const [loadingBulkChange, setLoadingBulkChange] = useState<LoadingConstants>(LoadingConstants.INITIAL);
  const [pageNumber, setPageNumber] = useState(1);

  const setSearchString = (cur: string) => {
    return setSearchStringState((prev) => {
      if (prev === '' && cur !== prev) setSelectedRows(new Set());
      return cur;
    });
  };

  const queryClient = useQueryClient();

  const { error: fetchingStatusesError, isLoading: isFetchingStatuses, data: statuses } = useGetEliseStatuses();

  const fetchedProperties = useMemo(() => statuses || [], [statuses]);

  const updateEliseAiStatusMutation = useAdManagementEliseAiApiHandler_updateEliseAiStatus({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AdManagementEliseAiApiHandler_getEliseStatuses_Path] });
    },
  });

  const updateAllEliseAiStatusesMutation = useAdManagementEliseAiApiHandler_updateAllEliseAiStatuses({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [AdManagementEliseAiApiHandler_getEliseStatuses_Path] });
      setLoadingBulkChange(LoadingConstants.SUCCESS);
    },
    onError: () => {
      setLoadingBulkChange(LoadingConstants.ERROR);
    },
  });

  const rowsPerPage = 10;
  const searchedProperties = useMemo(
    () =>
      searchString.length
        ? fetchedProperties.filter((p) =>
            Object.values({
              propertyName: p.propertyName,
              address: p.address,
            }).some((s) => s?.toLowerCase().includes(searchString)),
          )
        : fetchedProperties,
    [searchString, fetchedProperties],
  );

  const startNumber = rowsPerPage * (pageNumber - 1);
  const properties = searchedProperties.slice(startNumber, startNumber + rowsPerPage);

  const numShownProperties = properties.length;
  const numPages = Math.ceil(
    searchString.length ? searchedProperties.length / rowsPerPage : fetchedProperties.length / rowsPerPage,
  );
  const shownPropertiesString = `${(pageNumber - 1) * numShownProperties + 1}-${pageNumber * numShownProperties}`;

  const isAllSelected = selectedRows.size > 0;
  const isIndeterminate = selectedRows.size > 0 && selectedRows.size < properties.length;

  const handleRowSelection = (listingAlias: string, isSelected: boolean) => {
    const newSelectedRows = new Set(selectedRows);
    if (isSelected) {
      newSelectedRows.add(listingAlias);
    } else {
      newSelectedRows.delete(listingAlias);
    }
    setSelectedRows(newSelectedRows);
  };

  const handleSelectAll = (isSelected: boolean) => {
    if (isSelected) {
      if (searchString.length) setSelectedRows(new Set(searchedProperties.map((p) => p.listingAlias)));
      else setSelectedRows(new Set(fetchedProperties.map((p) => p.listingAlias)));
    } else {
      setSelectedRows(new Set());
    }
  };

  const MainContent = () => (
    <>
      <BulkActionsAndInfo
        fetchedProperties={fetchedProperties}
        searchedProperties={searchedProperties}
        isSearching={searchString.length > 0}
        shownPropertiesString={shownPropertiesString}
        selectedRows={selectedRows}
        setLoadingBulkChange={setLoadingBulkChange}
        updateAllEliseAiStatusesMutation={updateAllEliseAiStatusesMutation}
        updateEliseAiStatusMutation={updateEliseAiStatusMutation}
      />
      <LoadingMask
        loading={loadingBulkChange === LoadingConstants.LOADING}
        css={{ width: '100%', display: 'inline-table' }}
      >
        <EliseDataTable
          properties={properties}
          selectedRows={selectedRows}
          onRowSelection={handleRowSelection}
          onSelectAll={handleSelectAll}
          isAllSelected={isAllSelected}
          isIndeterminate={isIndeterminate}
          searchString={searchString}
          updateEliseAiStatusMutation={updateEliseAiStatusMutation}
        />
        <Pagination
          totalPages={numPages}
          currentPageIndex={pageNumber - 1}
          onPageSelected={(i: number) => setPageNumber(i + 1)}
          css={{ marginTop: 'md' }}
        />
      </LoadingMask>
    </>
  );

  const contentController = () => {
    if (isFetchingStatuses) {
      return <Spinner size="lg" />;
    }
    if (fetchingStatusesError || statuses === undefined) {
      return <Alert tone="critical">Error fetching statuses</Alert>;
    }
    return (
      <Box css={{ margin: 'sm' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'md', marginBottom: 'md' }}>
          <AdornedInput
            endAdornment={
              <AdornedInput.Adornment>
                <AdornedInput.Icon>
                  <IconSearchOutline />
                </AdornedInput.Icon>
              </AdornedInput.Adornment>
            }
            fluid
            input={
              <AdornedInput.Input
                placeholder="Search properties by name or address"
                onChange={(search) => setSearchString(search.target.value.toLowerCase())}
              />
            }
            size="md"
            startAdornment={null}
            css={{ backgroundColor: 'white' }}
          />
        </Box>
        <ToastProvider>
          <MainContent />
        </ToastProvider>
      </Box>
    );
  };

  return (
    <Box>
      <Box css={{ display: 'flex', flexDirection: 'row', alignItems: 'center', marginLeft: '24px', paddingTop: 'md' }}>
        <EliseImageContainer />
        <Box css={{ width: '100%', marginLeft: 'sm', marginRight: '0', marginBottom: '200' }}>
          <Box css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Text textStyle="heading-md">EliseAI</Text>
            <Button size="sm" onClick={() => window.open(MANAGE_ELISE_EXTERNAL_URL, '_blank')}>
              Manage data sharing{' '}
              <Icon size="sm">
                <IconExternalFilled />
              </Icon>
            </Button>
          </Box>
        </Box>
      </Box>

      <Page.Content css={{ margin: '0 auto' }}>
        <Text textStyle="body">
          Zillow&apos;s EliseAI integration uses an intelligent chatbot to respond to and nurture leads on your behalf
          &mdash; just connect your EliseAI account to Zillow and decide what data to share. For the chatbot to work,
          EliseAI needs to be connected and data sharing needs to be turned on.
        </Text>
      </Page.Content>
      <Page.Content>{contentController()}</Page.Content>
    </Box>
  );
};
